/** Foundation identity fields for order create / resolve payloads. */

export type PartyIdentity = {
  membershipId: string;
  globalUserId: string;
  organizationId: string;
};

export type OrderPartyIdentityPayload = {
  seller_org_id: string;
  seller_membership_id?: string;
  seller_global_user_id?: string;
  buyer_membership_id: string;
  buyer_org_id?: string;
  buyer_global_user_id?: string;
  customer_membership_id: string;
  customer_org_id?: string;
  customer_global_user_id?: string;
  agent_membership_id?: string | null;
  agent_org_id?: string | null;
  agent_global_user_id?: string | null;
  team_member_membership_id?: string | null;
  team_member_org_id?: string | null;
  team_member_global_user_id?: string | null;
  brokerage_firm_org_id?: string | null;
  agent_id?: string | null;
  team_member_id?: string | null;
};

export function strPartyId(value: unknown): string {
  if (value === undefined || value === null) return "";
  return String(value).trim();
}

export function emptyPartyIdentity(): PartyIdentity {
  return { membershipId: "", globalUserId: "", organizationId: "" };
}

/** Map `created-or-linked-detailed` row or normalized search item. */
export function identityFromSearchRow(
  item: Record<string, unknown> | null | undefined
): PartyIdentity {
  if (!item) return emptyPartyIdentity();
  const membershipId = strPartyId(
    item.membership_id ?? item.membershipId ?? item.value ?? item.id ?? item._id
  );
  const globalUserId = strPartyId(item.global_user_id ?? item.globalUserId);
  const organizationId = strPartyId(item.organization_id ?? item.organizationId);
  return { membershipId, globalUserId, organizationId };
}

/** Read identity from a selected option / agent / team member object. */
export function identityFromSelection(
  opt: Record<string, unknown> | null | undefined
): PartyIdentity {
  if (!opt) return emptyPartyIdentity();
  return {
    membershipId: strPartyId(
      opt.membershipId ?? opt.membership_id ?? opt.value ?? opt.id ?? opt._id
    ),
    globalUserId: strPartyId(opt.globalUserId ?? opt.global_user_id),
    organizationId: strPartyId(opt.organizationId ?? opt.organization_id),
  };
}

/** membership_id for GET /organization-memberships/{membership_id}/addresses */
export function resolveMembershipIdForAddresses(
  user?: {
    membershipId?: string;
    membership_id?: string;
    value?: string;
    id?: string;
    _id?: string;
  } | null,
  sessionFallback?: string | null
): string {
  return strPartyId(
    user?.membershipId ??
      user?.membership_id ??
      user?.value ??
      user?.id ??
      user?._id ??
      sessionFallback
  );
}

/** Id used for legacy `/user-addresses/:id` — prefer global user when available. */
export function addressOwnerIdFromSelection(
  opt: Record<string, unknown> | null | undefined
): string {
  if (!opt) return "";
  return strPartyId(
    opt.addressOwnerId ??
      opt.globalUserId ??
      opt.global_user_id ??
      opt.userId ??
      opt.user_id ??
      opt.membershipId ??
      opt.membership_id ??
      opt.value ??
      opt.id
  );
}

export function searchRowMembershipId(row: Record<string, unknown> | null | undefined): string {
  return identityFromSearchRow(row).membershipId;
}

/** Map membership search/select row to a react-select option with foundation ids. */
export function mapFoundationMembershipRowToOption(
  row: Record<string, unknown> | null | undefined,
  options?: { officeId?: string; label?: string }
) {
  const membershipId = strPartyId(
    row?.membership_id ?? row?.membershipId ?? row?.id ?? row?._id
  );
  const globalUserId = strPartyId(row?.global_user_id ?? row?.globalUserId);
  const organizationId = strPartyId(
    options?.officeId ?? row?.organization_id ?? row?.organizationId
  );
  const firstName = String(row?.first_name ?? row?.firstName ?? "").trim();
  const lastName = String(row?.last_name ?? row?.lastName ?? "").trim();
  const label =
    options?.label ||
    String(row?.name ?? "").trim() ||
    `${firstName} ${lastName}`.trim() ||
    String(row?.text ?? "") ||
    "(no name)";

  return {
    value: membershipId,
    membershipId,
    globalUserId,
    organizationId,
    label,
    firstName,
    lastName,
    email: String(row?.email ?? ""),
    phone: String(row?.phone1 ?? row?.phone ?? ""),
  };
}

function partyField(id: PartyIdentity) {
  return {
    membership_id: id.membershipId || undefined,
    org_id: id.organizationId || undefined,
    global_user_id: id.globalUserId || undefined,
  };
}

export function buildOrderPartyIdentityPayload(args: {
  seller: PartyIdentity;
  buyer: PartyIdentity;
  agent?: PartyIdentity | null;
  teamMember?: PartyIdentity | null;
  brokerageFirmOrgId?: string | null;
}): OrderPartyIdentityPayload {
  const buyer = args.buyer.membershipId ? args.buyer : emptyPartyIdentity();
  const agent = args.agent?.membershipId ? partyField(args.agent) : null;
  const team = args.teamMember?.membershipId ? partyField(args.teamMember) : null;
  const seller = args.seller;

  return {
    seller_org_id: seller.organizationId,
    ...(seller.membershipId ? { seller_membership_id: seller.membershipId } : {}),
    ...(seller.globalUserId ? { seller_global_user_id: seller.globalUserId } : {}),
    buyer_membership_id: buyer.membershipId,
    ...(buyer.organizationId ? { buyer_org_id: buyer.organizationId } : {}),
    ...(buyer.globalUserId ? { buyer_global_user_id: buyer.globalUserId } : {}),
    customer_membership_id: buyer.membershipId,
    ...(buyer.organizationId ? { customer_org_id: buyer.organizationId } : {}),
    ...(buyer.globalUserId ? { customer_global_user_id: buyer.globalUserId } : {}),
    agent_membership_id: agent?.membership_id ?? null,
    agent_org_id: agent?.org_id ?? null,
    agent_global_user_id: agent?.global_user_id ?? null,
    team_member_membership_id: team?.membership_id ?? null,
    team_member_org_id: team?.org_id ?? null,
    team_member_global_user_id: team?.global_user_id ?? null,
    brokerage_firm_org_id: strPartyId(args.brokerageFirmOrgId) || null,
    agent_id: agent?.membership_id ?? null,
    team_member_id: team?.membership_id ?? null,
  };
}
